import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Mic, BookOpen, HelpCircle, AlertTriangle, Award } from 'lucide-react'

const HomePage = () => {
  const quickActions = [
    {
      title: 'Getting Started',
      description: 'New to VivaLingua? Learn the basics of speaking English with AI.',
      href: '/getting-started',
      icon: Mic,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Level Guides',
      description: 'Interactive CEFR level guides from A1 to C2 with progress tracking.',
      href: '/guides?category=levels',
      icon: Award,
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Browse Guides',
      description: 'Comprehensive guides for mastering English conversation skills.',
      href: '/guides',
      icon: BookOpen,
      color: 'from-orange-500 to-red-500'
    },
    {
      title: 'FAQ',
      description: 'Find answers to the most common questions about AI language learning.',
      href: '/faq',
      icon: HelpCircle,
      color: 'from-green-500 to-teal-500'
    }
  ]

  const featuredGuides = [
    {
      title: 'First Conversation with AI',
      description: 'Step-by-step guide to having your first English conversation with AI',
      readTime: '5 min read',
      difficulty: 'Beginner',
      slug: 'first-conversation-with-ai'
    },
    {
      title: 'Common Interview Questions & Answers',
      description: 'Master 30 essential interview questions with professional sample answers',
      readTime: '15 min read',
      difficulty: 'Intermediate',
      slug: 'common-interview-questions'
    },
    {
      title: 'Business English Conversations',
      description: 'Practice professional English for workplace success',
      readTime: '12 min read',
      difficulty: 'Intermediate',
      slug: 'business-english-conversations'
    }
  ]

  const levelGuides = [
    { level: 'A1', title: 'Beginner', color: 'from-green-400 to-emerald-500' },
    { level: 'A2', title: 'Elementary', color: 'from-blue-400 to-cyan-500' },
    { level: 'B1', title: 'Intermediate', color: 'from-yellow-400 to-orange-500' },
    { level: 'B2', title: 'Upper-Inter.', color: 'from-purple-400 to-pink-500' },
    { level: 'C1', title: 'Advanced', color: 'from-red-400 to-rose-500' },
    { level: 'C2', title: 'Mastery', color: 'from-indigo-400 to-purple-600' }
  ]

  return (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <section className="relative px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Master <span className="gradient-text">English Speaking</span> with AI
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Complete guides, tips, and support for learning English conversation skills 
            using artificial intelligence. Start speaking with confidence today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/getting-started"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 group"
            >
              Get Started
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/guides"
              className="inline-flex items-center px-6 py-3 border border-slate-600 text-white font-semibold rounded-lg hover:bg-slate-800 transition-all duration-200"
            >
              Browse All Guides
            </Link>
          </div>
        </div>
      </section>

      {/* Level Guides Section */}
      <section className="px-4 py-16 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Choose Your Level</h2>
            <p className="text-slate-300 text-lg">Interactive CEFR level guides with progress tracking</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {levelGuides.map((level, index) => (
              <Link
                key={index}
                to={`/level/${level.level.toLowerCase()}`}
                className="group bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 border border-slate-700 hover:border-slate-600 transition-all duration-300 card-hover text-center"
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br ${level.color} rounded-lg mb-3`}>
                  <Award className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-blue-400 transition-colors">
                  {level.level}
                </h3>
                <p className="text-sm text-slate-400">{level.title}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">What would you like to do?</h2>
            <p className="text-slate-300 text-lg">Choose your path to English speaking mastery</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {quickActions.map((action, index) => {
              const Icon = action.icon
              return (
                <Link
                  key={index}
                  to={action.href}
                  className="group relative overflow-hidden rounded-xl bg-slate-800/50 p-6 backdrop-blur-sm border border-slate-700 hover:border-slate-600 transition-all duration-300 card-hover"
                >
                  <div className="flex items-start space-x-4">
                    <div className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br ${action.color} rounded-lg`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors">
                        {action.title}
                      </h3>
                      <p className="text-slate-300 mb-4">{action.description}</p>
                      <div className="flex items-center text-blue-400 font-medium">
                        <span>Learn more</span>
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* Featured Guides */}
      <section className="px-4 py-20 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Featured Guides</h2>
            <p className="text-slate-300 text-lg">Start with these popular guides to accelerate your learning</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredGuides.map((guide, index) => (
              <Link
                key={index}
                to={`/guides/${guide.slug}`}
                className="group bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-all duration-300 card-hover"
              >
                <div className="mb-4">
                  <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                    guide.difficulty === 'Beginner' ? 'bg-green-500/20 text-green-400' :
                    guide.difficulty === 'Intermediate' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {guide.difficulty}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-blue-400 transition-colors">
                  {guide.title}
                </h3>
                <p className="text-slate-300 mb-4">{guide.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">{guide.readTime}</span>
                  <ArrowRight className="h-4 w-4 text-blue-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/guides"
              className="inline-flex items-center px-6 py-3 border border-slate-600 text-white font-semibold rounded-lg hover:bg-slate-800 transition-all duration-200"
            >
              View All Guides
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default HomePage